import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

type JobListingTabTypes = 'recent' | 'week';

type JobsSearchParams = {
  page: number;
  type: JobListingTabTypes;
  serviceId: string;
  sortOrder: string;
  sortCol: string;
};

export const useJobsListing = (apiUrl: string, serviceId: string, pageSize: number = 10) => {
  const [urlParamsLoaded, setUrlParamsLoaded] = useState(false);
  const [searchParams, setSearchParams] = useState<JobsSearchParams>({
    page: 1,
    type: 'week',
    sortOrder: '1',
    sortCol: 'jobDate',
    serviceId,
  });

  const buildQueryString = useCallback((params: typeof searchParams) => {
    const urlParams = new URLSearchParams();
    urlParams.set('serviceId', params.serviceId.toString());
    urlParams.set('type', params.type.toString());
    urlParams.set('page', params.page.toString());
    urlParams.set('order', params.sortOrder);

    if (params.sortCol) {
      urlParams.set('orderCol', params.sortCol);
    }

    return `?${urlParams.toString()}`;
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(globalThis.location.search);
    const initialParams: JobsSearchParams = { ...searchParams };

    const urlPage = urlParams.get('page');
    if (urlPage) initialParams.page = Number.parseInt(urlPage, 10);

    const serviceId = urlParams.get('serviceId');
    if (serviceId) initialParams.serviceId = serviceId;

    const type = urlParams.get('type');
    if (type) initialParams.type = type as JobListingTabTypes;

    setSearchParams(initialParams);
    setUrlParamsLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listingQuery = useQuery({
    queryKey: ['jobsListing', searchParams],
    queryFn: async () => {
      const queryString = buildQueryString(searchParams);
      const apiParams = new URLSearchParams(queryString.substring(1));
      apiParams.set('pageSize', pageSize.toString());
      const apiQueryString = `?${apiParams.toString()}`;

      const url = `${apiUrl}${apiQueryString}`;

      const response = await fetch(url);

      if (!response.ok) throw new Error('Document listing fetch failed');
      return await response.json();
    },
    staleTime: 60000,
    enabled: urlParamsLoaded,
    placeholderData: keepPreviousData,
  });

  const updateType = useCallback((newType: JobListingTabTypes) => {
    setSearchParams(prev => {
      return {
        ...prev,
        type: newType,
      };
    });
  }, []);

  const updateUrl = useCallback(
    (params: typeof searchParams) => {
      const queryString = buildQueryString(params);
      globalThis.history.pushState({}, '', queryString);
    },
    [buildQueryString]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      const newParams = { ...searchParams, page };
      setSearchParams(newParams);
      updateUrl(newParams);
    },
    [searchParams, updateUrl]
  );

  const handleSortChange = useCallback(
    (newSortOrder: string, newSortCol: string) => {
      const newParams = { ...searchParams, sortOrder: newSortOrder, sortCol: newSortCol, page: 1 };
      setSearchParams(newParams);
      updateUrl(newParams);
    },
    [searchParams, updateUrl]
  );

  return {
    // Dynamic data for extensible table/filter rendering
    rows: listingQuery.data,

    // Tab get/set
    type: searchParams.type,
    updateType,

    // React Query states
    isLoading: listingQuery.isLoading,
    isError: listingQuery.isError,
    isSuccess: listingQuery.isSuccess,
    isFetching: listingQuery.isFetching,

    // Sort handling
    handleSortChange,

    // pagination data/control
    handlePageChange,
    pagination: {
      currentPage: searchParams.page,
      totalPages: listingQuery.data?.pagination?.totalPages ?? 1,
    },
    summary: {
      total: listingQuery.data?.summary?.total ?? null,
      sortCol: searchParams.sortCol ?? null,
      sortOrder: searchParams.sortOrder ?? '1',
    },
  };
};
